import {
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';

/**
 * Handle update API errors by:
 * 1. Always refetch on error to get latest state (server might have updated)
 * 2. If refetch succeeds, avoid reloading (data is already updated)
 * 3. If refetch also fails with 401/403, reload the page
 * 4. If refetch fails with other error, don't reload
 *
 * @param statusCode - HTTP status code from the update API response (undefined if success)
 * @param refetch - Function to refetch the current state
 * @returns true if page was reloaded, false otherwise
 */
export const handlePermissionError = async (
  statusCode: number | undefined,
  refetch: () => Promise<{ data?: { status?: number }; error?: unknown }>
): Promise<boolean> => {
  // Only handle errors (statusCode exists and is not success)
  if (!statusCode || isSuccess(statusCode)) {
    return false;
  }

  try {
    const refetchResult = await refetch();
    const refetchStatus = refetchResult.data?.status;

    // If refetch succeeds, don't reload - data is already updated
    if (isSuccess(refetchStatus)) {
      return false;
    }

    // If refetch fails with 401/403, reload page
    if (isAuthError(refetchStatus) || isForbiddenError(refetchStatus)) {
      location.reload();
      return true;
    }

    // If refetch fails with other error, don't reload
    return false;
  } catch {
    // If refetch throws an error, don't reload
    // (could be network error, etc.)
    return false;
  }
};
